import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {StudentNote} from '../student-note.model';
import {StudentNoteCategory} from '../student-note-category.model';
import {System} from '../../../shared-components/System';
import {ErrorList} from '../../../shared-components/controls/error-prompt/error-list.model';
import {StudentNotesService} from '../student-notes.service';
import {LoginService} from '../../../shared-modules/login/login.service';
import {Student} from '../../../shared-components/student/student.model';
import {StudentsToAddDialogComponent} from '../../../shared-components/students-to-add-dialog/students-to-add-dialog.component';
import {Subscription} from 'rxjs';
import {UserService} from '../../../shared-components/user/user.service';
import * as moment from 'moment';
import {YearService} from '../../../shared-components/year/year.service';

@Component({
    selector: 'add-edit-note-dialog',
    templateUrl: './add-edit-note-dialog.component.html',
    styleUrls: [
        './add-edit-note-dialog.component.css'
    ]
})
export class AddEditNoteDialogComponent implements OnInit, OnDestroy {

    // Injected Properties
    note: StudentNote;
    addNote: boolean;
    categories: StudentNoteCategory[] = [];
    junkDate = System.junkDate;
    multiAdd: boolean;
    multiStudents: Student[];

    // Local Properties
    errors: ErrorList[] = [];
    @ViewChild('categoryField', { read: ElementRef }) categoryField: ElementRef;
    @ViewChild('noteField', { read: ElementRef }) noteField: ElementRef;
    @ViewChild('orderField', { read: ElementRef }) orderField: ElementRef;
    @ViewChild('statusField', { read: ElementRef }) statusField: ElementRef;
    @ViewChild('startDateField', { read: ElementRef }) startDateField: ElementRef;
    @ViewChild('endDateField', { read: ElementRef }) endDateField: ElementRef;
    originalNote = new StudentNote();
    V10: boolean;
    subscriptions: Subscription[] = [];

    // Class Functions
    constructor(
        @Inject(MAT_DIALOG_DATA) private data: any,
        private dialogBox: MatDialogRef<any, any>,
        private studentNoteService: StudentNotesService,
        private loginService: LoginService,
        private dialog: MatDialog,
        private userSerivce: UserService,
        private yearService: YearService
    ) {
    }

    // Event Functions
    ngOnInit() {
        // POST: initializes the data
        this.V10 = this.loginService.LoginSettings.V10;

        // Getting injected data
        if (this.data) {
            this.note = this.data.note;
            this.addNote = this.data.addNote;
            this.categories = this.data.categories;
            this.multiAdd = this.data.multiAdd;
            this.multiStudents = this.data.multiStudents;
        }
        if (!this.categories) {
            this.subscriptions.push(
                this.studentNoteService.getStudentNotesCategories().subscribe(categories => {
                    this.categories = categories;
                })
            );
        }
        if (!this.note && (!this.multiAdd || !this.multiStudents)) {
            console.log('No student provided');
        }
        if (this.multiAdd) {
            this.note = new StudentNote();
            this.note.StartDate = null;
            this.note.EndDate = null;
        }

        // Setting up the local data
        Object.assign(this.originalNote, this.note);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    notePaste() {
        // PRE: text is pasted into the note
        // POST: truncates the text
    }

    save() {
        // PRE: the save button is clicked
        // POST: saves the note(s) and closes the dialog box
        // NOTE: closing with true here means there are new notes
        this.validate();
        if (this.errors.length == 0) {

            // Adding multiple notes
            if (this.multiAdd) {

                // Creating student notes for each student
                const notes: StudentNote[] = [];
                this.multiStudents.forEach(student => {
                    const note = new StudentNote();
                    Object.assign(note, this.note);
                    note.StudentID = student.Id;
                    note.StudentNumber = student.StudentNumber;
                    note.FirstName = student.FirstName;
                    note.MiddleName = student.MiddleName;
                    note.LastName = student.LastName;
                    note.Nickname = student.NickName;
                    notes.push(note);
                });

                // Service call
                this.studentNoteService.addStudentNotes(notes).subscribe(() => {
                    this.dialogBox.close(true);
                });
            }

            // Adding a singular note
            else if (this.addNote) {
                this.studentNoteService.addStudentNote(this.note).subscribe(
                    () => {
                        this.dialogBox.close(true);
                    }
                );
            }

            // Updating a singular note
            else {
                this.studentNoteService.updateStudentNotes([this.note]).subscribe(
                    () => {
                        this.note.LastUpOn = moment().format('MM/DD/YY');
                        this.note.LastUpBy = this.userSerivce.currentUser.LastName + ', ' + this.userSerivce.currentUser.FirstName;
                        this.dialogBox.close(false);
                    }
                );
            }
        }
    }

    cancel() {
        // PRE: the cancel button is clicked
        // POST: resets the note and closes the dialog box
        if (!this.addNote) {
            Object.assign(this.note, this.originalNote);
        }
        this.dialogBox.close();
    }

    viewStudents() {
        // PRE: the "multiple students" link is clicked
        // POST: opens the dialog to view those students
        this.dialog.open(StudentsToAddDialogComponent, {
            disableClose: true,
            data: {
                students: this.multiStudents
            }
        });
    }

    // Methods
    validate() {
        // POST: sets the errors
        this.errors = [];

        // Category is required
        if (!this.note.SNCID) {
            this.errors.push({
                fieldName: 'Category',
                fieldRef: this.categoryField,
                errorMsg: 'Field is required.'
            });
        }

        // Note is required
        if (!this.note.Note) {
            this.errors.push({
                fieldName: 'Note',
                fieldRef: this.noteField,
                errorMsg: 'Field is required.'
            });
        }

        // Order is required
        if (!this.note.Order) {
            this.errors.push({
                fieldName: 'Order',
                fieldRef: this.orderField,
                errorMsg: 'Field is required.'
            });
        }

        // Order must be a number
        if (this.note.Order && typeof this.note.Order != 'number') {

            // Correcting for string entries
            if (!Number.isNaN(Number.parseInt(this.note.Order, 10))) {
                this.note.Order = Number.parseInt(this.note.Order, 10);
            }

            // Catching error if it's a word
            else {
                this.errors.push({
                    fieldName: 'Order',
                    fieldRef: this.orderField,
                    errorMsg: 'Value must be a number.'
                });
            }
        }

        // Status is required
        if (!this.note.Status) {
            this.errors.push({
                fieldName: 'Status',
                fieldRef: this.statusField,
                errorMsg: 'Field is required.'
            });
        }

        // End Date must be after Start Date
        if (this.note.StartDate && this.note.EndDate && this.note.StartDate > this.note.EndDate) {
            this.errors.push({
                fieldName: 'Dates',
                fieldRef: this.startDateField,
                errorMsg: 'The End Date must be on or after the Start Date.'
            });
        }
    }
}
